import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import GameCard from '@/components/base/GameCard';
import ArticleCard from '@/components/base/ArticleCard';
import { games } from '@/mocks/games';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faq';

const hotGames = games.filter((g) => g.isHot).slice(0, 4);
const newGames = games.filter((g) => g.isNew).slice(0, 4);
const featuredGames = games.filter((g) => g.featured).slice(0, 3);
const latestArticles = articles.slice(0, 3);

export default function Home() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const homepageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '黄油在线玩 - 免费畅玩黄油游戏平台',
    description: '黄油在线玩平台提供最全黄油游戏免费在线体验，无需下载安装，打开浏览器即可畅玩。海量黄油游戏资源，每日更新，支持PC与手机端。',
    url: 'https://huangyouzaxianwan.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '黄油在线玩',
      url: 'https://huangyouzaxianwan.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(homepageJsonLd)}</script>
      <Navbar />
      <main>
        <section className="relative w-full h-[500px] md:h-[650px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20colorful%20abstract%20gaming%20background%20with%20warm%20golden%20and%20orange%20gradients%2C%20soft%20geometric%20shapes%2C%20playful%20arcade%20game%20atmosphere%2C%20modern%20digital%20art%20style%2C%20bright%20joyful%20colors%2C%20smooth%20flowing%20abstract%20design%2C%20warm%20inviting%20tone&width=1800&height=800&seq=hero-bg-home&orientation=landscape"
            alt="黄油在线玩"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-background-50 mb-4 leading-tight">
              黄油在线玩
            </h1>
            <p className="text-base md:text-xl text-background-100/90 mb-3 max-w-2xl mx-auto leading-relaxed">
              免费黄油游戏在线平台，海量黄油游戏无需下载
            </p>
            <p className="text-sm md:text-lg text-background-200/80 mb-8 max-w-xl mx-auto">
              打开浏览器即可畅玩，每日更新，支持PC与手机端
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/rpg"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                立即开始游戏
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-100/30 rounded-full text-sm font-medium hover:bg-background-50/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">{games.length}+</p>
                <p className="text-xs text-background-200/70 mt-1">精品游戏</p>
              </div>
              <div className="w-px h-10 bg-background-100/30"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">500万+</p>
                <p className="text-xs text-background-200/70 mt-1">活跃玩家</p>
              </div>
              <div className="w-px h-10 bg-background-100/30"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-background-50">每日更新</p>
                <p className="text-xs text-background-200/70 mt-1">新游不断</p>
              </div>
            </div>
          </div>
        </section>

        <section id="featured" className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
                精选推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-2">编辑精选，必玩神作</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredGames.map((game) => (
                <Link
                  key={game.id}
                  to={`/category/${game.categorySlug}`}
                  className="group relative w-full aspect-[16/10] rounded-lg overflow-hidden cursor-pointer"
                >
                  <img
                    src={game.image}
                    alt={game.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <span className="px-2 py-0.5 text-xs bg-accent-500 text-background-50 rounded-md">精选</span>
                    <h3 className="text-lg md:text-xl font-heading text-background-50 mt-2">{game.title}</h3>
                    <p className="text-xs text-background-200/80 mt-1 line-clamp-1">{game.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">热门游戏</h2>
                <p className="text-sm text-foreground-500 mt-2">当前最受玩家欢迎的游戏</p>
              </div>
              <Link
                to="/category/action"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
              {hotGames.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">游戏分类</h2>
              <p className="text-sm text-foreground-500 mt-2">找到你喜欢的游戏类型</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-5 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer group"
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-full ${
                    cat.color === 'accent' ? 'bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50' :
                    cat.color === 'secondary' ? 'bg-secondary-100 text-secondary-600 group-hover:bg-secondary-500 group-hover:text-background-50' :
                    'bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50'
                  } transition-all duration-300`}>
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <div className="text-center">
                    <h3 className="text-sm font-semibold text-foreground-800">{cat.name}</h3>
                    <p className="text-xs text-foreground-400 mt-0.5">{cat.count}款游戏</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">新游上线</h2>
                <p className="text-sm text-foreground-500 mt-2">最新上线的精品游戏</p>
              </div>
              <Link
                to="/category/strategy"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
              {newGames.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">最新文章</h2>
                <p className="text-sm text-foreground-500 mt-2">游戏攻略、评测与资讯</p>
              </div>
              <Link
                to="/article/top-10-butter-games-2025"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                更多文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="flex flex-col gap-4">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-accent-500">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-background-50 mb-4">
              随时随地畅玩黄油游戏
            </h2>
            <p className="text-base text-background-100/90 mb-8 leading-relaxed">
              无需下载安装，打开浏览器即可畅玩。海量黄油游戏资源，每日更新，支持PC与手机端，让你随时随地享受黄油在线玩的极致乐趣。
            </p>
            <Link
              to="/category/rpg"
              className="inline-flex px-8 py-3 bg-background-50 text-accent-600 rounded-full text-sm font-semibold hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              开始免费畅玩
            </Link>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">常见问题</h2>
              <p className="text-sm text-foreground-500 mt-2">关于黄油在线玩的常见疑问</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-background-100 rounded-lg border border-background-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <i className={`text-foreground-400 transition-transform duration-300 text-sm flex-shrink-0 ${openFaq === faq.id ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </button>
                  {openFaq === faq.id && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}